import React, { useEffect } from 'react';
import { Link } from '../routing';
import { Sparkles } from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';
import TraderIdentity from '../components/TraderIdentity';
import PageHero from '../components/PageHero';
import theme from '../theme';

// Falls back to the support address when no dedicated privacy mailbox is set,
// because a data subject must have somewhere that answers.
const PRIVACY_EMAIL = process.env.REACT_APP_PRIVACY_EMAIL || 'privacy@glamgo.hair';
const STRIPE_PRIVACY_URL = 'https://stripe.com/privacy';
const ANTHROPIC_PRIVACY_URL = 'https://www.anthropic.com/legal/privacy';
const LINK = { color: theme.gold };

// Inline markup used inside translated sentences. Keeping the email address and
// external URL here (rather than inside the strings) means they are never
// duplicated across locales or accidentally translated.
const COMPONENTS = {
  b: <strong />,
  i: <em />,
  br: <br />,
  mail: <a href={`mailto:${PRIVACY_EMAIL}`} style={LINK}>{PRIVACY_EMAIL}</a>,
  stripe: <a href={STRIPE_PRIVACY_URL} target="_blank" rel="noreferrer" style={LINK}>Stripe</a>,
  anthropic: <a href={ANTHROPIC_PRIVACY_URL} target="_blank" rel="noreferrer" style={LINK}>Anthropic</a>,
};

function T({ k }) {
  return <Trans i18nKey={k} components={COMPONENTS} />;
}

export default function PrivacyPolicy() {
  const { t } = useTranslation();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div style={{ fontFamily: theme.font.body, background: theme.cream, minHeight: '100vh' }}>
      {/* Navbar */}
      <nav style={{ background: theme.espresso, padding: '20px 40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none' }}>
          <Sparkles size={18} color={theme.gold} />
          <span style={{ fontFamily: theme.font.display, fontSize: 24, fontWeight: 600, color: theme.white, letterSpacing: '0.05em' }}>
            GLAM<span style={{ color: theme.gold }}>GO</span>
          </span>
        </Link>
        <Link to="/" style={{ color: theme.alpha.white(0.6), textDecoration: 'none', fontSize: 12, letterSpacing: '0.12em' }}>{t('privacy.back')}</Link>
      </nav>

      {/* Header */}
      <PageHero
        eyebrow={t('privacy.label')}
        title={t('privacy.title')}
        subtitle={t('privacy.lastUpdated', { date: t('privacy.updatedDate') })}
        height={280}
      />

      {/* Content */}
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '64px 24px' }}>
        <Section title={t('privacy.s1.title')}>
          <p><T k="privacy.s1.p1" /></p>
          {/* GDPR Art. 13 requires the controller to be identified. The policy
              never said who that was — this is the section where it belongs. */}
          <p><T k="privacy.s1.p1b" /></p>
          <p><T k="privacy.s1.p2" /></p>
        </Section>

        <Section title={t('privacy.s2.title')}>
          <p><T k="privacy.s2.intro" /></p>
          <ul>
            <li><T k="privacy.s2.i1" /></li>
            <li><T k="privacy.s2.i2" /></li>
            <li><T k="privacy.s2.i3" /></li>
            <li><T k="privacy.s2.i4" /></li>
            <li><T k="privacy.s2.i5" /></li>
            <li><T k="privacy.s2.i6" /></li>
            <li><T k="privacy.s2.i7" /></li>
          </ul>
        </Section>

        <Section title={t('privacy.s3.title')}>
          <p><T k="privacy.s3.intro" /></p>
          <ul>
            <li><T k="privacy.s3.i1" /></li>
            <li><T k="privacy.s3.i2" /></li>
            <li><T k="privacy.s3.i3" /></li>
            <li><T k="privacy.s3.i4" /></li>
            <li><T k="privacy.s3.i5" /></li>
            <li><T k="privacy.s3.i6" /></li>
            <li><T k="privacy.s3.i7" /></li>
          </ul>
          <p><T k="privacy.s3.outro" /></p>
        </Section>

        <Section title={t('privacy.s4.title')}>
          <p><T k="privacy.s4.intro" /></p>
          <ul>
            <li><T k="privacy.s4.i1" /></li>
            <li><T k="privacy.s4.i2" /></li>
            <li><T k="privacy.s4.i3" /></li>
            <li><T k="privacy.s4.i4" /></li>
          </ul>
          <p><T k="privacy.s4.transfers" /></p>
          <p><T k="privacy.s4.outro" /></p>
        </Section>

        <Section title={t('privacy.s5.title')}>
          <p><T k="privacy.s5.intro" /></p>
          <ul>
            <li><T k="privacy.s5.i1" /></li>
            <li><T k="privacy.s5.i2" /></li>
            <li><T k="privacy.s5.i3" /></li>
            <li><T k="privacy.s5.i4" /></li>
            <li><T k="privacy.s5.i5" /></li>
            <li><T k="privacy.s5.i6" /></li>
          </ul>
          <p style={{ marginTop: 20 }}><T k="privacy.s5.suppression" /></p>
          <p><T k="privacy.s5.erase" /></p>
          {/* Until 2026-08-10 the periods above were enforced by nothing at all:
              the only scheduled DELETE in the codebase was for the locker
              webhook forensics, so every row ever written was kept forever while
              this page said otherwise. Now that a nightly job applies them, the
              page can say so — and saying so is what makes it checkable. */}
          <p><T k="privacy.s5.enforced" /></p>
        </Section>

        <Section title={t('privacy.s6.title')}>
          <p><T k="privacy.s6.intro" /></p>
          <ul>
            <li><T k="privacy.s6.i1" /></li>
            <li><T k="privacy.s6.i2" /></li>
            <li><T k="privacy.s6.i3" /></li>
            <li><T k="privacy.s6.i4" /></li>
            <li><T k="privacy.s6.i5" /></li>
            <li><T k="privacy.s6.i6" /></li>
          </ul>
          <p><T k="privacy.s6.outro" /></p>
        </Section>

        <Section title={t('privacy.s7.title')}>
          <p><T k="privacy.s7.p1" /></p>
        </Section>

        <Section title={t('privacy.s8.title')}>
          <p><T k="privacy.s8.p1" /></p>
        </Section>

        <Section title={t('privacy.s9.title')}>
          <p><T k="privacy.s9.p1" /></p>
        </Section>

        {/* Art. 13(1)(c) wants the legal basis for each purpose, and Art.
            13(2)(d) wants the data subject told they can complain to the
            supervisory authority. Neither was anywhere in this policy: it listed
            what we do and which rights exist, and stopped there. */}
        <Section title={t('privacy.s11.title')}>
          <p><T k="privacy.s11.intro" /></p>
          <ul>
            <li><T k="privacy.s11.i1" /></li>
            <li><T k="privacy.s11.i2" /></li>
            <li><T k="privacy.s11.i3" /></li>
            <li><T k="privacy.s11.i4" /></li>
          </ul>
          <p><T k="privacy.s11.outro" /></p>
        </Section>

        <Section title={t('privacy.s12.title')}>
          <p><T k="privacy.s12.p1" /></p>
        </Section>

        <Section title={t('privacy.s10.title')}>
          <p><T k="privacy.s10.p1" /></p>
          <TraderIdentity />
        </Section>
      </div>

    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 48 }}>
      <h2 style={{ fontFamily: theme.font.display, fontSize: 26, color: theme.ink, fontWeight: 500, margin: '0 0 16px', borderBottom: `1px solid ${theme.sand}`, paddingBottom: 12 }}>{title}</h2>
      <div style={{ color: theme.ink600, fontSize: 15, lineHeight: 1.8 }}>{children}</div>
    </div>
  );
}

